import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { PICFLOW_CONFIG } from '../picflow-config';
import { DataLoaderService } from './data-loader.service';
import { LanguageService } from './language.service';

export type TextReplace = { key: string; value: string };

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  private languageService = inject(LanguageService);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  textCache: Map<string, string> = new Map();
  contextsLoaded: Set<string> = new Set();
  contextLoadingErrors: Set<string> = new Set();

  private config = inject(PICFLOW_CONFIG);

  constructor() {
    this.languageService.current.subscribe(() => {
      this.load();
      this.textCache.clear();
      this.contextsLoaded.clear();
    });
  }

  private async load() {
    const lang = this.languageService.current.getValue();
    if (this.contextsLoaded.has(lang)) return;
    if (this.contextLoadingErrors.has(lang)) return;
    this.contextsLoaded.add(lang);
    const resp = await this.loader.get(lang, async () => {
      try {
        const version = (typeof window !== 'undefined' && window['picflowVersion']) || Math.random();

        return await lastValueFrom(
          this.http.get(`${this.config.translationBase}/assets/translations/${lang}.json?v=${version}`, {
            responseType: 'json',
          })
        );
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        return;
      }
    });

    if (resp) {
      Object.keys(resp).forEach(key => {
        this.textCache.set(`${key}`, resp[key]);
      });
    } else {
      this.contextsLoaded.delete(lang);
      this.contextLoadingErrors.add(lang);
    }
  }

  async get(key: string, replaces: TextReplace[] = []) {
    await this.load();
    const text = this.textCache.get(key) ?? key;
    return this.transformText(text, replaces);
  }

  getSync(key: string, replaces: TextReplace[] = []) {
    const text = this.textCache.get(key) ?? key;
    return this.transformText(text, replaces);
  }

  private transformText(text: string, replaces: TextReplace[]) {
    replaces.forEach(replace => (text = text.split(replace.key).join(replace.value)));
    return text;
  }
}
